// Remembered accounts — a small, NON-SENSITIVE convenience list of the usernames this
// browser has successfully signed in as (GitHub-style "Accounts on this device").
//
// SECURITY: this stores ONLY display identifiers (username/handle/display + alias) and a
// timestamp. NEVER tokens, refresh tokens, DPoP keys, or WebAuthn credential IDs. It is a
// pure UX affordance so the user can tap a known account instead of typing. Losing or
// tampering with this list grants no authentication capability whatsoever.

const LS_ACCOUNTS = 'vid.accounts';
const MAX_ACCOUNTS = 5;

export interface RememberedAccount {
  /** Primary display identifier used to seed handle-based login (username or handle). */
  username: string;
  /** Optional friendlier alias / display name. */
  alias?: string;
  /** Epoch millis of last successful sign-in as this account. */
  lastUsedAt: number;
}

function read(): RememberedAccount[] {
  try {
    const raw = localStorage.getItem(LS_ACCOUNTS);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return [];
    return arr
      .filter((a): a is RememberedAccount => !!a && typeof a.username === 'string' && a.username.length > 0)
      .map((a) => ({
        username: String(a.username),
        alias: a.alias != null ? String(a.alias) : undefined,
        lastUsedAt: Number(a.lastUsedAt) || 0,
      }));
  } catch {
    return [];
  }
}

function write(list: RememberedAccount[]): void {
  try {
    localStorage.setItem(LS_ACCOUNTS, JSON.stringify(list));
  } catch {
    /* storage may be full/blocked — remembered accounts are best-effort */
  }
}

/** Return remembered accounts, most-recently-used first. */
export function listAccounts(): RememberedAccount[] {
  return read().sort((a, b) => b.lastUsedAt - a.lastUsedAt);
}

/**
 * Record a successful sign-in. De-dupes by username (case-insensitive), refreshes
 * lastUsedAt, orders most-recent-first, and caps the list. No-op if no usable
 * username/handle is available (never throws — safe to call from any success path).
 */
export function rememberAccount(a: { username?: string | null; handle?: string | null; display?: string | null; alias?: string | null }): void {
  const username = (a.username || a.handle || a.display || '').toString().trim();
  if (!username) return; // nothing displayable to remember; don't crash
  const alias = (a.alias || a.display || '').toString().trim() || undefined;

  const key = username.toLowerCase();
  const others = read().filter((x) => x.username.toLowerCase() !== key);
  const next: RememberedAccount[] = [{ username, alias, lastUsedAt: Date.now() }, ...others]
    .sort((x, y) => y.lastUsedAt - x.lastUsedAt)
    .slice(0, MAX_ACCOUNTS);
  write(next);
}

/** Remove a remembered account by username (case-insensitive). */
export function forgetAccount(username: string): void {
  const key = (username || '').toLowerCase();
  write(read().filter((x) => x.username.toLowerCase() !== key));
}
